/**
 * Shared insertion of an MText annotation at a world position.
 *
 * Every text-producing tool ends the same way: create the MText, style it
 * from the active NoteConfig, push one undoable command, select it. This
 * lives here so TextDragger (drag & drop), HandwritingDragger (draw2text)
 * and StylusKeyboardDragger (device handwriting keyboard) share one path.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import { trackShape, type TextVia } from "@/analytics/drawing-analytics";
import type { DrawingModuleType } from "../DrawingSingleton";
import type { NoteConfig, Textstyle } from "@/types";
import { AddEntityCommand } from "../commands/AddEntityCommand";
import { DEFAULT_TEXT_STYLE } from "../database/textStyle";
import { createTextAtPosition } from "../managers/creation";
import { applyEntityProps } from "../entities";
import { getEntityHandle } from "../core/oda";
import { deleteObj } from "../core";
import { selectionService } from "../selection";

export interface TextInsertionOptions {
  /** Insertion point (top-left of the text), in world coordinates. */
  worldX: number;
  worldY: number;
  text: string;
  /** Active note config: gives the text style, size, layer and props. */
  config: NoteConfig;
  /** Select the created entity (so the user can move/edit it right away). */
  select?: boolean;
  /** Label for the viewer update, for debugging. */
  source?: string;
  /** How the text content was produced, for analytics (absent = untracked). */
  via?: TextVia;
}

export function insertTextEntity(
  viewer: DRDrawingViewer,
  module: DrawingModuleType,
  options: TextInsertionOptions,
): void {
  const db = viewer.database;
  if (!db) {
    throw new Error("insertTextEntity: db is not defined");
  }

  const { worldX, worldY, text, config, select = true, source, via } = options;
  const textstyle: Textstyle = config.props.textstyle || DEFAULT_TEXT_STYLE;

  const entityId = createTextAtPosition(
    module,
    db,
    worldX,
    worldY,
    text,
    textstyle,
    config.props.textsize,
  );

  applyEntityProps(module, db, entityId, config.props);

  const command = new AddEntityCommand(viewer, {
    entityHandle: getEntityHandle(entityId),
    originalLayerName: config.props.layername ?? "0",
  });
  viewer.executeCommand(command);
  if (via) trackShape("text", { via });
  viewer.update(source ?? "insertTextEntity");

  if (select) {
    selectionService.selectEntity(entityId, module, db);
    viewer.update(source ?? "insertTextEntity");
  }

  deleteObj(entityId);
}
